import { IconButton, Modal, Typography } from "@material-ui/core";
import { AddOutlined, CloseOutlined, RemoveOutlined } from "@material-ui/icons";
import { format, isValid } from "date-fns";
import React, { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { getClientNameForOrder } from "../../../utils/getClientNameForOrder";
import { Items, KitchenOrder } from "../../../types";
import { formatCurrency } from "../../../utils/format";
import sumValuesFromAnArray from "../../../utils/sumValuesFromAnArray";

import { Button } from "../../index";

import useStyles from "./styles";
import { useUpdateOrder } from "../../../api/orders/hooks";
import { useStore } from "../../../store";
import { useEntity } from "../../../hooks/useEntity";

export interface EditRequestProps {
  open: boolean;
  order: KitchenOrder;
  onClose: () => void;
  disableProductsActionButtons?: boolean;
  readOnly?: boolean;
}

export const EditRequest: React.FC<EditRequestProps> = ({
  open,
  order,
  onClose,
  disableProductsActionButtons,
  readOnly = false,
}) => {
  const Styles = useStyles();

  const queryClient = useQueryClient();
  const mutator = useUpdateOrder();

  const [items, setItems] = useState<Items>([]);

  const { showObservations } = useEntity();
  const { userManager } = useStore();

  let scheduleFor: string | null = null;

  if (order.isSchedule) {
    scheduleFor = order.scheduleFor as string;
  } else {
    scheduleFor = null;
  }

  const handleSaveItems = useCallback(async () => {
    if (readOnly) return;
    onClose();

    await mutator.mutateAsync({
      items,
      id: order.id,
      scheduleFor,
    });

    await queryClient.invalidateQueries(["allOrders", userManager?.entity]);
    await queryClient.invalidateQueries(["allOrdersScheduleOrder", userManager?.entity]);
  }, [items, readOnly]);

  const handleChangeAmount = (id: string, amount: number) => {
    if (readOnly) return;
    setItems((_items) =>
      _items.map((item) => {
        const currentItem = item;

        if (currentItem.id === id) {
          if (
            amount >= 1 &&
            amount <=
              (order.items.find((_item) => _item.id === item.id)?.maxAmount ||
                1)
          ) {
            currentItem.quantity = amount;
            currentItem.totalAmount = currentItem.price * amount;
          }
        }

        return currentItem;
      }));
  };

  const handleRemoveItem = (id: string) => {
    if (readOnly) return;
    setItems((_items) =>
      _items.map((item) => {
        const currentItem = item;

        if (currentItem.id === id) {
          currentItem.quantity = 0;
          currentItem.totalAmount = 0;
        }

        return currentItem;
      }));
  };

  const getTotalAmount = useCallback(
    () =>
      formatCurrency(
        sumValuesFromAnArray(items.map((item) => item.totalAmount / 100))
      ),
    [JSON.stringify(items)]
  );

  const handleClose = () => {
    onClose();

    setItems([]);
  };

  useEffect(() => {
    if (order.items.length > 0) {
      setItems(
        order.items.map((_item) => ({
          id: _item.id,
          name: _item.name,
          quantity: _item.quantity,
          price: _item.unitPrice ? _item.unitPrice : _item.price,
          totalAmount: _item.totalAmount,
          observation: _item.observation,
        }))
      );
    }
  }, [order.id]);

  return (
    <Modal className={Styles.modal} open={open} onClose={onClose}>
      <div className={Styles.modalView} role="dialog" aria-modal="true" aria-labelledby="order-details-title">
        <div className={Styles.header}>
          <div className={Styles.heading}>
            <Typography id="order-details-title" component="h2" className={Styles.title}>
              {readOnly ? "Detalhes do pedido" : "Editar pedido"}
            </Typography>
            <Typography className={Styles.customer}>{getClientNameForOrder(order)}</Typography>
            {order.createdAt && isValid(new Date(order.createdAt)) && (
              <Typography className={Styles.date}>
                {format(new Date(order.createdAt), "dd/MM/yyyy 'às' HH:mm")}
              </Typography>
            )}
          </div>
          <IconButton aria-label="Fechar detalhes do pedido" onClick={handleClose} className={Styles.closeButton}>
            <CloseOutlined />
          </IconButton>
        </div>
        {readOnly && <Typography className={Styles.readOnlyNotice}>Somente consulta · Pedido de dia anterior</Typography>}
        <Typography component="h3" className={Styles.sectionTitle}>Itens do pedido</Typography>
        <div className={Styles.items}>
          {items
            .filter((_item) => _item.quantity > 0)
            .map((item) => (
              <div key={item.id} className={Styles.item}>
                <div className={Styles.amount}>
                  {!readOnly && (
                    <IconButton
                      aria-label={`Diminuir quantidade de ${item.name}`}
                      className={Styles.icon}
                      onClick={() =>
                        handleChangeAmount(item.id, item.quantity - 1)}
                    >
                      <RemoveOutlined />
                    </IconButton>
                  )}

                  <Typography component="span">{item.quantity}</Typography>

                  {!readOnly && !disableProductsActionButtons && (
                    <IconButton
                      aria-label={`Aumentar quantidade de ${item.name}`}
                      className={Styles.icon}
                      onClick={() =>
                        handleChangeAmount(item.id, item.quantity + 1)}
                    >
                      <AddOutlined />
                    </IconButton>
                  )}
                </div>

                {showObservations && item.observation ? (
                  <div className={Styles.details}>
                    <Typography component="span" className={Styles.name}>
                      {item.name}
                    </Typography>
                    <div className={Styles.observation}>
                      <Typography component="span">
                        Observação:
                        {' '}
                        {item.observation}
                      </Typography>
                    </div>
                  </div>
                ) : (
                  <Typography component="span" className={Styles.name}>
                    {item.name}
                  </Typography>
                )}

                <Typography component="span" className={Styles.price}>
                  {formatCurrency(item.totalAmount / 100)}
                </Typography>

                {!readOnly && (
                  <IconButton
                    aria-label={`Remover ${item.name}`}
                    className={Styles.icon}
                    onClick={() => handleRemoveItem(item.id)}
                  >
                    <CloseOutlined />
                  </IconButton>
                )}
              </div>
            ))}
        </div>

        <div className={Styles.footer}>
          <div className={Styles.total}>
            <Typography component="span">Total</Typography>

            <Typography component="span">{getTotalAmount()}</Typography>
          </div>

          <div className={Styles.actions}>
            <Button color="secondary" size="large" onClick={handleClose}>
              {readOnly ? "Fechar" : "Cancelar"}
            </Button>

            {!readOnly && (
              <Button color="primary" size="large" onClick={handleSaveItems}>
                Salvar
              </Button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
